/**
 * SOVRANOVA Offline Memory Cache
 * ------------------------------------------------------------
 * Keeps the user's memory reads (profile, greeting, history,
 * journal, outcomes) available when the device is offline OR
 * while the backend is mid-deploy/upgrade. Fully transparent:
 * api.js writes here on every successful GET and falls back to
 * here when the network is unreachable.
 *
 * Design rules:
 *  - Per-user namespaced keys so one user's memory never leaks
 *    to another on a shared device.
 *  - Heavy binary fields (audio) are stripped before storing.
 *  - Oversized payloads are skipped (localStorage quota guard).
 *  - Nothing in here ever throws — caching must never break the app.
 */

import { encryptToString, decryptFromString } from './deviceCrypto';

const PREFIX = 'sovcache:';
const MAX_BYTES = 250 * 1024; // skip caching payloads larger than ~250KB
const STRIP_FIELDS = ['audio', 'mimeType']; // never persist TTS audio

// Paths whose GET responses represent durable user memory.
const CACHEABLE = [
  '/auth/profile',
  '/memory/context',
  '/outcomes/summary',
  '/welcome',
  '/journal/entries',
  '/session/greet',
  '/session/history',
];

export function isCacheablePath(path) {
  if (!path) return false;
  const clean = path.split('?')[0];
  return CACHEABLE.some(p => clean === p || clean.startsWith(p));
}

function keyFor(userId, path) {
  return `${PREFIX}${userId || 'anon'}:${path}`;
}

function sanitize(value) {
  if (!value || typeof value !== 'object') return value;
  const copy = Array.isArray(value) ? [...value] : { ...value };
  for (const f of STRIP_FIELDS) {
    if (f in copy) delete copy[f];
  }
  return copy;
}

export async function writeCache(userId, path, value) {
  try {
    const enc = await encryptToString(sanitize(value));
    if (!enc) return; // can't encrypt -> never store plaintext (cache is disposable)
    const record = JSON.stringify({ t: Date.now(), c: enc });
    if (record.length > MAX_BYTES) return; // too big — skip, don't risk quota
    try {
      localStorage.setItem(keyFor(userId, path), record);
    } catch (_) {
      // quota exceeded — free the oldest entry and retry once
      try { pruneOldest(userId); localStorage.setItem(keyFor(userId, path), record); } catch (_) {}
    }
  } catch (_) {}
}

export async function readCache(userId, path) {
  try {
    const raw = localStorage.getItem(keyFor(userId, path));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.c !== undefined) {
      const v = await decryptFromString(parsed.c);
      if (v === null) return null; // key gone / corrupt -> treat as cache miss
      return { value: v, cachedAt: parsed.t };
    }
    // Legacy plaintext entry written before encryption — still readable.
    return { value: parsed.v, cachedAt: parsed.t };
  } catch (_) {
    return null;
  }
}

// Remove the single oldest cached entry for this user (quota relief).
function pruneOldest(userId) {
  try {
    const ns = `${PREFIX}${userId || 'anon'}:`;
    let oldestKey = null, oldestT = Infinity;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(ns)) {
        try { const t = JSON.parse(localStorage.getItem(k)).t || 0; if (t < oldestT) { oldestT = t; oldestKey = k; } } catch (_) {}
      }
    }
    if (oldestKey) localStorage.removeItem(oldestKey);
  } catch (_) {}
}

// Clear ALL cached memory (every user). Called on sign-out.
export function clearAllCache() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach(k => localStorage.removeItem(k));
  } catch (_) {}
}
